/**
 * Кодирование штрихкода в полосы для печати ценника (карточка 102). Перенос
 * `frontend/src/shared/barcode/barcode.ts` старого клиента без изменений
 * логики: печать ценников 1С в новом клиенте должна давать те же полосы.
 *
 * Своё, без зависимостей: EAN-13 и EAN-8 — это таблица из трёх наборов по
 * десять строк, Code 128 — таблица из 107 шаблонов. Библиотека ради этого тянула
 * бы свои форматы вывода и шрифты, а ценнику нужны только полосы.
 *
 * Результат — строка модулей: `1` — полоса, `0` — просвет, каждый символ — одна
 * ширина модуля. Рисует её `BarcodeSvg`.
 *
 * Вид кода выбирается по самому значению — так же, как его понимает сервер
 * (`BarcodeClassifier`): 13 цифр с верной контрольной суммой — EAN-13, 8 — EAN-8,
 * 12 цифр — EAN-13 с досчитанной контрольной цифрой. Всё остальное печатное —
 * Code 128. Непечатное (пусто, кириллица) — `null`: этикетка выйдет без
 * штрихкода, но выйдет.
 */

export type BarcodeKind = 'ean13' | 'ean8' | 'code128'

export interface EncodedBarcode {
  kind: BarcodeKind
  /** Полосы и просветы по модулю: `1` — полоса, `0` — просвет. */
  modules: string
  /** Цифры под штрихкодом — ровно то, что закодировано. */
  text: string
}

// --- EAN ---------------------------------------------------------------------

/** Наборы кодирования цифры: L и G — левая половина, R — правая. */
const EAN_L = ['0001101', '0011001', '0010011', '0111101', '0100011', '0110001', '0101111', '0111011', '0110111', '0001011']
const EAN_G = ['0100111', '0110011', '0011011', '0100001', '0011101', '0111001', '0000101', '0010001', '0001001', '0010111']
const EAN_R = ['1110010', '1100110', '1101100', '1000010', '1011100', '1001110', '1010000', '1000100', '1001000', '1110100']

/** Какой набор (L/G) у каждой из шести левых цифр EAN-13 — по первой цифре. */
const EAN13_PARITY = ['LLLLLL', 'LLGLGG', 'LLGGLG', 'LLGGGL', 'LGLLGG', 'LGGLLG', 'LGGGLL', 'LGLGLG', 'LGLGGL', 'LGGLGL']

/**
 * Контрольная цифра EAN/GTIN для цифр БЕЗ неё: справа налево веса 3, 1, 3, 1…
 * Тот же алгоритм, что `BarcodeValidator` на сервере.
 */
export function eanCheckDigit(digits: string): number {
  let sum = 0
  for (let i = 0; i < digits.length; i++) {
    const digit = Number(digits[digits.length - 1 - i])
    sum += i % 2 === 0 ? digit * 3 : digit
  }
  return (10 - (sum % 10)) % 10
}

function hasValidCheck(code: string): boolean {
  return eanCheckDigit(code.slice(0, -1)) === Number(code.at(-1))
}

function encodeEan13(code: string): string {
  const first = Number(code[0])
  const parity = EAN13_PARITY[first]!
  let modules = '101'
  for (let i = 1; i <= 6; i++) {
    const digit = Number(code[i])
    modules += parity[i - 1] === 'L' ? EAN_L[digit] : EAN_G[digit]
  }
  modules += '01010'
  for (let i = 7; i <= 12; i++) modules += EAN_R[Number(code[i])]
  return `${modules}101`
}

function encodeEan8(code: string): string {
  let modules = '101'
  for (let i = 0; i < 4; i++) modules += EAN_L[Number(code[i])]
  modules += '01010'
  for (let i = 4; i < 8; i++) modules += EAN_R[Number(code[i])]
  return `${modules}101`
}

// --- Code 128 ----------------------------------------------------------------

/**
 * Шаблоны Code 128: ширины чередующихся полосы и просвета в модулях, начиная с
 * полосы. Значения 0–102 — символы, 103–105 — старт A/B/C, 106 — стоп (13 модулей).
 */
export const CODE128_PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
] as const

const START_B = 104
const START_C = 105
const STOP = 106

/**
 * Значения символов Code 128: набор C (пары цифр — вдвое короче) для чисто
 * цифровой строки чётной длины, иначе набор B (печатный ASCII). Переключение
 * наборов внутри кода не делаем: на ценнике такие коды не встречаются, а
 * простота здесь важнее выигранных миллиметров.
 *
 * Возвращает `null`, если в строке есть символ вне печатного ASCII.
 */
export function code128Values(text: string): number[] | null {
  if (/^\d+$/.test(text) && text.length % 2 === 0) {
    const values: number[] = [START_C]
    for (let i = 0; i < text.length; i += 2) values.push(Number(text.slice(i, i + 2)))
    return withChecksum(values)
  }
  const values: number[] = [START_B]
  for (const char of text) {
    const code = char.charCodeAt(0)
    if (code < 32 || code > 126) return null
    values.push(code - 32)
  }
  return withChecksum(values)
}

/** Контрольный символ: старт + сумма «значение × позиция» по модулю 103. */
function withChecksum(values: number[]): number[] {
  let sum = values[0]!
  for (let i = 1; i < values.length; i++) sum += values[i]! * i
  return [...values, sum % 103, STOP]
}

function patternToModules(pattern: string): string {
  let modules = ''
  for (let i = 0; i < pattern.length; i++) modules += (i % 2 === 0 ? '1' : '0').repeat(Number(pattern[i]))
  return modules
}

// --- выбор вида ----------------------------------------------------------------

/** Закодировать значение штрихкода товара. `null` — печатать нечего. */
export function encodeBarcode(value: string | null | undefined): EncodedBarcode | null {
  const text = (value ?? '').trim()
  if (text === '') return null

  if (/^\d{13}$/.test(text) && hasValidCheck(text)) {
    return { kind: 'ean13', modules: encodeEan13(text), text }
  }
  // UPC-A без ведущего нуля и EAN-13 без контрольной цифры: 12 цифр —
  // досчитываем, как прототип этикетки (карточка 102).
  if (/^\d{12}$/.test(text)) {
    const full = `${text}${eanCheckDigit(text)}`
    return { kind: 'ean13', modules: encodeEan13(full), text: full }
  }
  if (/^\d{8}$/.test(text) && hasValidCheck(text)) {
    return { kind: 'ean8', modules: encodeEan8(text), text }
  }

  const values = code128Values(text)
  if (values === null) return null
  return { kind: 'code128', modules: values.map((v) => patternToModules(CODE128_PATTERNS[v]!)).join(''), text }
}
